/**
 * assets.js — Central asset registry for Cleopatra's Fire
 *
 * Defines all textures, spine animations and effects this game loads.
 * Trimmed 2026-09-28 to what the game actually uses: every block that was
 * dead behind an OFF flag, primary-theme only, or Mad Labs only (gamble,
 * Golden Mystery) was removed rather than left gated. Turning a feature on
 * again means registering its assets here again.
 *
 * Exports:
 *   ASSETS       — Master object consumed by assetsSlice for loading
 *   ASSET_NAME   — Flat { name → url } map for direct cache lookups
 *   getUITexture  — Spritesheet-aware UI texture retrieval
 *   getUITextureAsDataURL — Extract spritesheet sub-texture as base64 (for HTML/CSS)
 */

import * as PIXI from "@pixi/assets";
import { symbolData } from "./SymbolData.js";
import { getAssetsPath, getBasePath } from "./utils/pathConfig";
import { FEATURES_CONFIG } from "./featuresConfig";
import { Settings } from "./settings";

// ---------------------------------------------------------------------------
// Path constants
// ---------------------------------------------------------------------------
const BASE_PATH = getAssetsPath();
const SPRITESHEETS_PATH = `${getBasePath()}/assets/spritesheets`;
const UI_THEME = Settings.APP.UI_THEME;

// ---------------------------------------------------------------------------
// 1. UI SPRITESHEET
// ---------------------------------------------------------------------------
const UI_ASSETS = [
  {
    name: "ui_spritesheet_new",
    url: `${SPRITESHEETS_PATH}/user_interface/user_interface.json`,
  },
];

// ---------------------------------------------------------------------------
// 2. UI TEXTURE HELPERS
// ---------------------------------------------------------------------------

/**
 * Get a UI texture from the theme spritesheet by frame name.
 * @param {string} name - Frame name (e.g., "icon_home", "popup_button")
 * @returns {PIXI.Texture|null}
 */
export const getUITexture = (name) => {
  // Try the current theme spritesheet first
  const key = "ui_spritesheet";
  if (PIXI.Assets.cache.has(key)) {
    const sheet = PIXI.Assets.get(key);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) return tex;
    }
  }
  // Fallback to new spritesheet (migration)
  const newKey = "ui_spritesheet_new";
  if (PIXI.Assets.cache.has(newKey)) {
    const sheet = PIXI.Assets.get(newKey);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) return tex;
    }
  }
  // Spritesheet not loaded yet — try individually loaded asset
  return PIXI.Assets.cache.has(name) ? PIXI.Assets.get(name) : null;
};

/**
 * Get a UI texture from the NEW spritesheet only.
 * @param {string} name - Frame name
 * @returns {PIXI.Texture|null}
 */
export const getNewUITexture = (name) => {
  const key = "ui_spritesheet_new";
  if (PIXI.Assets.cache.has(key)) {
    const sheet = PIXI.Assets.get(key);
    if (sheet?.textures) {
      const tex = sheet.textures[name] || sheet.textures[`${name}.png`];
      if (tex) {
        // Enable mipmaps + anisotropic filtering for UI textures — they are
        // static spritesheet sprites that get downscaled. Safe because these
        // are NOT dynamic canvas textures (PIXI.Text) which cause black flash.
        if (tex.baseTexture && tex.baseTexture.mipmap !== 2) {
          tex.baseTexture.mipmap = 2; // MIPMAP_MODES.ON — works on non-pow2
          tex.baseTexture.anisotropicLevel = 8; // Sharper at non-uniform scales
        }
        return tex;
      }
    }
  }
  return null;
};

/**
 * Extract a PIXI texture's frame to a base64 data URL via an offscreen canvas.
 * Shared by the UI-spritesheet and standalone-image helpers below.
 * @param {PIXI.Texture|null} texture
 * @param {string} [mime="image/png"] - e.g. "image/png" | "image/webp"
 * @returns {string} data URL or empty string
 */
const textureToDataURL = (texture, mime = "image/png") => {
  if (!texture?.baseTexture?.resource?.source) return "";
  const frame = texture.frame;
  const canvas = document.createElement("canvas");
  canvas.width = frame.width;
  canvas.height = frame.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    texture.baseTexture.resource.source,
    frame.x,
    frame.y,
    frame.width,
    frame.height,
    0,
    0,
    frame.width,
    frame.height,
  );
  return canvas.toDataURL(mime);
};

/**
 * Extract a UI spritesheet sub-texture as a base64 data URL.
 * Useful for HTML <img> tags or CSS background-image.
 * @param {string} name - Frame name
 * @returns {string} data URL or empty string
 */
export const getUITextureAsDataURL = (name) =>
  textureToDataURL(getUITexture(name));

/**
 * Same as getUITextureAsDataURL but reads the NEW UI spritesheet
 * (ui_spritesheet_new) — for icons like icon_autoplay_stop used by the
 * secondary-UI buttons, in HTML <img>/CSS contexts.
 * @param {string} name - Frame name
 * @returns {string} data URL or empty string
 */
export const getNewUITextureAsDataURL = (name) =>
  textureToDataURL(getNewUITexture(name));

/**
 * Extract a preloaded standalone game image (non-spritesheet) as a data URL.
 * Used by HTML <img> tags in React overlays (paytable, bonus buy, etc.).
 * Returns empty string if the asset hasn't been loaded yet.
 * @param {string} name - Asset name as registered in GAME_TEXTURES (e.g., "grand_jackpot")
 * @returns {string} data URL or empty string
 */
export const getGameImageSrc = (name) => {
  try {
    if (PIXI.Assets.cache.has(name)) {
      const url = textureToDataURL(PIXI.Assets.get(name), "image/webp");
      if (url) return url;
    }
  } catch {
    // Fall through to the plain URL below.
  }
  // ── The cache is an OPTIMISATION, not the source ──
  //
  // These are <img> tags in a React overlay: a URL is all they ever needed. The
  // data URL exists so the paytable opens with no fetch, and reaching for it
  // FIRST is right — but returning "" when it misses turned a warm-cache
  // optimisation into a hard requirement, and a slide went blank with nothing
  // in the console to say why.
  //
  // A miss is normal: an asset added after the bundle was loaded, a load that
  // failed silently (the texture loop swallows errors on purpose so one bad
  // file cannot stop the rest), or an overlay opened before preloading ended.
  // In every one of those the file is still there to be fetched.
  const entry = ASSETS.textures.find((a) => a.name === name);
  return entry ? entry.url : "";
};

// ---------------------------------------------------------------------------
// 3. GAME TEXTURES — Backgrounds, logos, paylines, feature graphics
// ---------------------------------------------------------------------------
const GAME_TEXTURES = [
  // Logo
  { name: "logo", url: `${BASE_PATH}/images/logo.webp` },
  // The trails' head flame (LOGO_POT.TRAIL_FLAME): the logo's fire frames as a
  // spritesheet with one animation, `flame` (2026-10-06).
  // { name: "flame_trail", url: `${BASE_PATH}/effects/trail/flame_trail.json` },
  // ONE effect now (2026-10-06): effects/flame_trail — its atlas carries the
  // fire particle AND the 30 flame frames (animation `flame`).
  { name: "flame_trail", url: `${BASE_PATH}/effects/flame_trail/flame_trail_atlas.json` },
  // The loader's logo (LoaderController PRELOADER_IMAGES.LOGO, 2026-10-02).
  // The loader force-loads it itself (it is up before this bundle finishes);
  // registered here so the cache and resolver know it.
  { name: "loader_logo", url: `${BASE_PATH}/images/loader_logo.webp` },

  // ── Buy Bonus card art — one image per buy level (GDD §10) ──
  //
  // In the order the popup lists them, and the art says which is which:
  //   1  purple, 7/8/9 spins, no pips     -> featureLevel "1", from Level 0
  //   2  red,    7/8/9 spins, two pips    -> featureLevel "2", from Level 2
  //   3  gold,   8/9/10 spins, four pips  -> featureLevel "3", from Level 4
  //   4  one of each behind a "?"         -> featureLevel "4", the random one
  ...(FEATURES_CONFIG.BONUS_BUY.ENABLED
    ? [
        // Mad Labs' buy-card art — removed with its files (2026-09-30); the
        // cards show the coin / scatter pictures (BuyBonusPopup symbolImage):
        // { name: "bonus_buy_1", url: `${BASE_PATH}/images/bonus_buy_1.webp` },
        // { name: "bonus_buy_2", url: `${BASE_PATH}/images/bonus_buy_2.webp` },
        // { name: "bonus_buy_3", url: `${BASE_PATH}/images/bonus_buy_3.webp` },
        // { name: "bonus_buy_4", url: `${BASE_PATH}/images/bonus_buy_4.webp` },
      ]
    : []),

  // Secondary theme paytable images — preloaded during loading screen so the
  // paytable opens instantly with no on-demand fetches.
  ...(UI_THEME === "secondary"
    ? [
        // Feature slides
        // ── Mad Labs symbols, for the PAYTABLE ONLY ──
        //
        // Flat art rather than the reels' spine textures: the extractor's are
        // sized for a reel cell, so blown up to a paytable row they are soft.
        //
        // `paytable_` on most of them because the bare name belongs to the
        // SPINE — every reel symbol is registered as `{ name: spineName }`
        // below, so an image called `h1` loses the name to the skeleton and
        // getGameImageSrc returns "". `wild` and `scatter` need no prefix; no
        // spine is called either.
        //
        // NOTHING ELSE READS THESE. The board keeps using the spines.
        // REMOVED 2026-09-30 with their files — the paytable uses the spine
        // pictures now (Vladimir: "use what we have"):
        // { name: "wild", url: `${BASE_PATH}/images/wild.webp` },
        // { name: "paytable_h1", url: `${BASE_PATH}/images/h1.png` },
        // { name: "paytable_m1", url: `${BASE_PATH}/images/m1.webp` },
        // { name: "paytable_m2", url: `${BASE_PATH}/images/m2.webp` },
        // { name: "paytable_m3", url: `${BASE_PATH}/images/m3.webp` },
        // { name: "paytable_l1", url: `${BASE_PATH}/images/l1.webp` },
        // { name: "paytable_l2", url: `${BASE_PATH}/images/l2.webp` },
        // { name: "paytable_l3", url: `${BASE_PATH}/images/l3.webp` },
        // { name: "paytable_l4", url: `${BASE_PATH}/images/l4.webp` },
        // { name: "scatter", url: `${BASE_PATH}/images/scatter.webp` },
        // Symbol slide 1 & 2
        // Paylines (both orientations — we don't know orientation at load time)
        // {
        //   name: "paylines_portrait",
        //   url: `${BASE_PATH}/images/paylines_portrait.webp`,
        // },
        // One picture for both orientations (2026-10-13); the portrait file is
        // deleted.
        {
          name: "paylines_landscape",
          url: `${BASE_PATH}/images/paylines_landscape.webp`,
        },
        // Decorative
        { name: "glow", url: `${BASE_PATH}/images/glow.webp` },
        // The paytable's Royal Jackpot picture (2026-10-02, SYMBOL_SLIDE_ROWS).
        {
          name: "paytable_royal_jackpot",
          url: `${BASE_PATH}/images/paytable_royal_jackpot.webp`,
        },
        // The bonus intro cards' own backgrounds (2026-10-12) — FeatureIntroCard
        // (Free Spins intro + retrigger, Hold & Win intro), the orientation's
        // one. They replace the intro screen background that stood in.
        {
          name: "free_spins_intro_background_portrait",
          url: `${BASE_PATH}/images/free_spins_intro_background_portrait.webp`,
        },
        {
          name: "free_spins_intro_background_landscape",
          url: `${BASE_PATH}/images/free_spins_intro_background_landscape.webp`,
        },
        {
          name: "hold_and_win_intro_background_portrait",
          url: `${BASE_PATH}/images/hold_and_win_intro_background_portrait.webp`,
        },
        {
          name: "hold_and_win_intro_background_landscape",
          url: `${BASE_PATH}/images/hold_and_win_intro_background_landscape.webp`,
        },
        // Intro screen — composed layout assets.
        //
        // ONE background, not two. The composed popup reads
        // intro_background_landscape in both orientations (BG_ALIAS in
        // IntroPopupComposedController); intro_background_portrait had no
        // reader left once Pot Collection was switched off, so it is gone.
        ...(!FEATURES_CONFIG.INTRO_SCREEN.SPINE_ENABLED
          ? [
              {
                name: "intro_background_landscape",
                url: `${BASE_PATH}/images/intro_background_landscape.webp`,
              },
              // Painted in portrait (2026-10-02); the layout still measures
              // the landscape one (IntroPopupComposedController).
              {
                name: "intro_background_portrait",
                url: `${BASE_PATH}/images/intro_background_portrait.webp`,
              },
              // Zeus's split pot + coins pairs. The intro slider does NOT show
              // these any more — its slides are the startpage_image_* set
              // below. They stay registered because the PAYTABLE still draws
              // them (PaytableOverlay / paytableConfig) until its own art
              // arrives. intro_feature_superturbo is the exception: it is a
              // live intro slide, the fourth one.
              {
                name: "intro_feature_superturbo",
                url: `${BASE_PATH}/images/intro_feature_superturbo.webp`,
              },
              // Intro slider feature images. One image per slide — no split
              // pot/coins pairs in this game.
              {
                name: "startpage_image_1",
                url: `${BASE_PATH}/images/startpage_image_1.webp`,
              },
              {
                name: "startpage_image_2",
                url: `${BASE_PATH}/images/startpage_image_2.webp`,
              },
              // Page 2's second image — the silver chest (2026-10-02).
              {
                name: "startpage_image_2_1",
                url: `${BASE_PATH}/images/startpage_image_2_1.webp`,
              },
              {
                name: "startpage_image_3",
                url: `${BASE_PATH}/images/startpage_image_3.webp`,
              },
              // Pages 4 and 5 (2026-10-02) — super turbo is page 6.
              {
                name: "startpage_image_4",
                url: `${BASE_PATH}/images/startpage_image_4.webp`,
              },
              {
                name: "startpage_image_5",
                url: `${BASE_PATH}/images/startpage_image_5.webp`,
              },
              // zeus_respins_logo is NOT registered. Its only reader was the
              // Pot Collection intro dialog, which this game does not run.
            ]
          : []),
      ]
    : []),
];

// ---------------------------------------------------------------------------
// 4. SYMBOL SPINES — Auto-generated from SymbolData
// ---------------------------------------------------------------------------
const symbolSpines = Object.keys(symbolData)
  .filter((key) => {
    const symbol = symbolData[key];
    return (
      symbol?.spine &&
      typeof symbol.spine === "string" &&
      symbol.spine.trim().length > 0
    );
  })
  .map((key) => {
    const spineName = symbolData[key].spine;
    // Guard against corrupted spine names (e.g., HTML responses cached in dev)
    if (/[<>]|DOCTYPE|html|window|response/.test(spineName)) return null;
    // Almost every symbol spine lives in spines/reel_symbol/. `spineDir` is
    // the opt-out, for skeletons that are registered THROUGH symbolData —
    // because a texture has to be captured from them — but are not reel art.
    // The gamble level card is the one today: it lives in spines/gamble_level/
    // and there is a second, older copy of it still sitting in reel_symbol/
    // that nothing must load. The atlas follows the json path automatically
    // (assetsSlice swaps the extension), so the folder only has to be stated
    // once.
    const spineDir = symbolData[key].spineDir || "reel_symbol";
    return {
      name: spineName,
      json: `${BASE_PATH}/spines/${spineDir}/${spineName}.json`,
    };
  })
  .filter(Boolean)
  .filter(
    (spine, i, self) => i === self.findIndex((s) => s.name === spine.name),
  );

// ---------------------------------------------------------------------------
// 5. SPINE ANIMATIONS — Conditionally built from feature flags
// ---------------------------------------------------------------------------
const buildSpineAnimations = () => {
  const spines = [];

  // -- Core (always loaded) ------------------------------------------------
  if (FEATURES_CONFIG.GAME_VIEW.ENABLED) {
    // GameView spine replaces background_animation + reel_background.
    // Logo is embedded inside game_view spine — standalone logo spine not needed.
    // intro_screen spine only loaded when INTRO_SCREEN.SPINE_ENABLED — secondary theme uses static fallback.
    spines.push(
      {
        name: "game_view",
        json: `${BASE_PATH}/spines/game_view/game_view.json`,
      },
      {
        name: "win_celebration",
        json: `${BASE_PATH}/spines/win_celebration/win_celebration.json`,
      },
      // NOT LOADED (Cleopatra's Fire) — the Mad Labs bonus room, replaced by
      // free_spins_game_view:
      // {
      //   name: "bonus_gameview",
      //   json: `${BASE_PATH}/spines/bonus_gameview/bonus_gameview.json`,
      // },
      {
        name: "free_spins_game_view",
        json: `${BASE_PATH}/spines/free_spins_game_view/free_spins_game_view.json`,
      },
      // The Cleopatra Hold & Win room — HoldAndWinGameView.
      {
        name: "hold_and_win_game_view",
        json: `${BASE_PATH}/spines/hold_and_win_game_view/hold_and_win_game_view.json`,
      },
      // REPLACED by the animator's own export below (2026-09-29). This was the
      // hand-extracted counter + collector on the room's atlas — it breaks the
      // moment the new game view replaces that atlas.
      // {
      //   name: "hold_and_win_counter",
      //   json: `${BASE_PATH}/spines/hold_and_win_counter/hold_and_win_counter.json`,
      //   textureless: true,
      // },
      // The respin counter — its own spine and atlas (animator, 2026-09-29).
      // Injected into the room's `respin_counter_placeholder` slot
      // (HoldAndWinCounterController).
      {
        name: "hold_and_win_respin_counter",
        json: `${BASE_PATH}/spines/hold_and_win_respin_counter/hold_and_win_respin_counter.json`,
      },
      // The End Collector — its own spine and atlas (animator, 2026-09-29).
      // Registered, not used yet: the counter turns into it at the end
      // (counter_transform_*), a later step.
      {
        name: "hold_and_win_collector",
        json: `${BASE_PATH}/spines/hold_and_win_collector/hold_and_win_collector.json`,
      },
      // The bonus intro card is static art now (FeatureIntroCard: the
      // startpage board images, paytable_royal_jackpot and the intro
      // background), not a spine.
      {
        // name: "hold_and_win_intro",
        // json: `${BASE_PATH}/spines/hold_and_win_intro/hold_and_win_intro.json`,
        // Renamed `feature_spins` (2026-10-01): the FS / H&W reload and the FS
        // retrigger popup (YOU HAVE / n / ... LEFT, YOU WON / +n / ...).
        name: "feature_spins",
        json: `${BASE_PATH}/spines/feature_spins/feature_spins.json`,
      },
      // Hold & Win's total win (animator, 2026-09-30): in / idle / out
      // (JSON fixes: `in`/`ou` were swapped — now in/out; tap slot renamed txt_slot_tap_to_continue), value + xbet + tap slots. Drawn by
      // FreeSpinTotalWinController in its Hold & Win mode. Tap bone hand-edited.
      {
        // name: "hold_and_win_outro",
        // json: `${BASE_PATH}/spines/hold_and_win_outro/hold_and_win_outro.json`,
        // Renamed `feature_outro` (2026-10-01): THE total win of both
        // features, free spins and Hold & Win.
        name: "feature_outro",
        json: `${BASE_PATH}/spines/feature_outro/feature_outro.json`,
      },
      // The free-spins Max Win and Better Luck (zero win) popups — their own
      // spines now (animator, 2026-09-30; win_celebration dropped the skins).
      // FreeSpinMaxWinController / FreeSpinZeroWinController. JSON hand edits
      // noted there (tap slot, zero_win's clip names and atlas page).
      {
        name: "max_win",
        json: `${BASE_PATH}/spines/max_win/max_win.json`,
      },
      {
        name: "zero_win",
        json: `${BASE_PATH}/spines/zero_win/zero_win.json`,
      },
      // The locked-row plate (animator, 2026-09-30): idle / out. One per
      // locked row, in the H&W room's attachment_locked_reel_1..4 slots
      // (HoldAndWinGameViewController.attachLockedRows).
      {
        name: "locked_reel",
        json: `${BASE_PATH}/spines/locked_reel/locked_reel.json`,
      },
      // Reel anticipation (animator, 2026-09-29): reel_anticipation_in / _loop
      // / _out. A frame over the whole board, up for the whole anticipation
      // chain, in the rooms' `attachment_reel_anticipation` slot
      // (GameViewSimpleController.attachReelAnticipation).
      {
        name: "reel_anticipation",
        json: `${BASE_PATH}/spines/reel_anticipation/reel_anticipation.json`,
      },
      // Per-reel scatter/coin anticipation — its own spine now (animator,
      // 2026-09-29), played in the rooms' `attachment_anticipation` slot
      // (GameViewSimpleController.attachAnticipation). Centred on its column.
      {
        name: "anticipation",
        json: `${BASE_PATH}/spines/anticipation/anticipation.json`,
      },
      // Injected into both rooms' `logo_placeholder` slot. Centred on its own
      // origin — the slot does all the placing and scaling.
      {
        name: "logo",
        json: `${BASE_PATH}/spines/logo/logo.json`,
      },
      {
        name: "transition",
        json: `${BASE_PATH}/spines/transition/transition.json`,
      },
    );
  }

  // -- iOS fullscreen overlay ----------------------------------------------
  if (FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED) {
    spines.push({
      name: "ios_scroll",
      json: `${BASE_PATH}/spines/ios_scroll/ios_scroll.json`,
    });
  }

  // -- Win Box -------------------------------------------------------------
  // Zeus's entry. A fire frame per winning cell (base and free spins).
  if (FEATURES_CONFIG.WIN_BOX.ENABLED) {
    spines.push({ name: "win_box", json: `${BASE_PATH}/spines/win_box/win_box.json` });
  }

  // -- Win Lines -----------------------------------------------------------
  // Hand-built from the game's own fire line (anticipation's ant_fire_line):
  // a path rig, one `win_line_<i>` clip per payline (2026-10-13).
  if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
    spines.push({ name: "win_lines", json: `${BASE_PATH}/spines/win_lines/win_lines.json` });
  }

  // -- Win Line Digits -----------------------------------------------------
  if (FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED) {
    // spines.push({
    //   // Textureless: pure animation data, no atlas and no page image. The
    //   // skeleton animates a bone and a slot's colour; the visible number is a
    //   // PIXI.Text the app pins to that bone. See WinTextController.
    //   name: "winline_text",
    //   json: `${BASE_PATH}/spines/winline_text/winline_text.json`,
    //   textureless: true,
    // });
    // Renamed `basic_win` (2026-10-05), now with its own art (skins fire /
    // no_fire — `fire` for now). Same use: the board's win numbers
    // (WinTextController) and the coin counter (GameViewSimpleController).
    spines.push({
      name: "basic_win",
      json: `${BASE_PATH}/spines/basic_win/basic_win.json`,
    });
    // The coin counter's own copy of it (2026-10-22): its text grows and
    // fades in with the fire, and pulses gently in the loop. Skeleton only —
    // built on basic_win's atlas (GameViewSimpleController._coinCounterSpine).
    spines.push({
      name: "coin_counter",
      json: `${BASE_PATH}/spines/coin_counter/coin_counter.json`,
      textureless: true,
    });
  }

  // -- Free spin intro (Mad Labs) ------------------------------------------
  // One skin per level won, so the popup shows the package the player got.
  // Also loaded for the dev panel, which triggers this popup on its own while
  // the feature is still off. DEV_PANEL is dev-only, so a release pays nothing.
  if (
    FEATURES_CONFIG.FREE_SPINS.ENABLED ||
    FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL
  ) {
    // Mad Labs' two popups — replaced by `hold_and_win_intro` (the FS intro,
    // reload and retrigger all play it now, 2026-09-30); their files are gone.
    spines.push(
      // {
      //   name: "free_spin_intro_dialog",
      //   json: `${BASE_PATH}/spines/free_spin_intro_dialog/free_spins_intro_dialog.json`,
      // },
      // Shown mid-feature when more spins are won. One animation per case
      // rather than one skin per level, so it carries no skins at all.
      //
      // The FILE is `additional_freespins_popups`, the ASSET is still
      // `additional_free_spin_dialog`. The animator renamed the export; the
      // alias is what every consumer holds, so it stays.
      // {
      //   name: "additional_free_spin_dialog",
      //   json: `${BASE_PATH}/spines/additional_free_spin_dialog/additional_freespins_popups.json`,
      // },
    );
  }

  // -- Symbol spines (auto-generated from SymbolData) ----------------------
  spines.push(...symbolSpines);

  return spines;
};

// ---------------------------------------------------------------------------
// 6. BITMAP FONTS
// ---------------------------------------------------------------------------
// Bitmap fonts were primary-theme only. This game is secondary and draws all
// of its text with styled PIXI.Text, so it loads none.
const buildBitmapFonts = () => [];

// ---------------------------------------------------------------------------
// 7. EFFECTS — RevoltFX particle bundles
// ---------------------------------------------------------------------------
const buildEffects = () => {
  // ── TWO bundles, because two are what this game fires ──
  //
  //   trail.json     `trail_blue`, `trail_gold` — the dart. Fired by
  //                  LevelChargingAnimator: a scatter charging its free-spin
  //                  level box, and the Golden Mystery's multiplier reaching the
  //                  coins it multiplies. Loads into its own FX instance (see
  //                  the particle_trail_effect branch in assetsSlice).
  //
  //   lightning.json   `Lightning`, `LightningBall` — the bolt. It came from
  //                  Zeus and was named for it; the name is gone because the
  //                  effect is not Zeus's any more — Mad Labs fires it.
  //                  Its particles are MovieClips on loop, which is where the
  //                  crackle comes from; a stretched still frame cannot have it,
  //                  which is why the first attempt at this was thrown away. In
  //                  the Golden Mystery every coin the COLLECTOR takes fires one
  //                  into it — see Engine/Effects/fireCollectBolts, which is
  //                  fireLightningBolt reversed and without its energy balls.
  //                  Loads into the MAIN instance, which is what makes
  //                  FXManager.isReady true and emitLightning available.
  //
  // ── Its `spritesheetFilter` is NOT empty, and must not be ──
  //
  // RevoltFX ends `initBundle` with `parseTextureCache(spritesheetFilter)`, and
  // that scans PIXI's WHOLE global texture cache — not the bundle's own sheet.
  // It also builds MovieClips by name: `mc_<group>_<n>.png` grouped on
  // `split("_")[1]`.
  //
  // BOTH atlases used to name a group `lightning`: this bolt is
  // `mc_lightning_Animation_0..11` (74x459, vertical) and the trail bundle carried
  // `mc_lightning_1..9` (about 300x100, HORIZONTAL). With an empty filter the
  // main instance parsed both and its `lightning` MovieClip held all 21 frames,
  // so the bolt flickered between vertical and horizontal art — which on screen
  // is a cross, not a strike.
  //
  // Fixed at BOTH ends, 2026-08-21:
  //
  //   1. the nine lightning frames are gone from the trail's own atlas.
  //      Nothing referenced them — its own emitters draw `fx_dot.png` — and
  //      lightning comes straight from Zeus now. The pixels stay in the PNG;
  //      an atlas entry is what makes a frame exist.
  //   2. this bundle's filter is `mc_lightning_Animation`, every frame it owns
  //      and none that it does not, so a re-export that puts lightning back into
  //      trail.json cannot poison the bolt again. Both of its emitters use that
  //      one MovieClip, so nothing else is lost.
  //
  // ── What used to be here as well ──
  //
  // rain and coin_shower, both behind POT_COLLECTION, which is off in this game:
  // dead weight in the list, so they are gone rather than gated. The lightning
  // came out with them for the same reason and is back on its own merit — a
  // feature that ships in Mad Labs now fires it.
  //
  // NEITHER is feature-gated. The mystery is core to this game and fires both in
  // the BASE game, so gating on FREE_SPINS would take them from a feature that
  // still needs them.
  // ── Cleopatra's Fire (2026-09-29): ONE bundle ──
  //
  // Only the coin → logo fire trail is fired in this game. Mad Labs' dart
  // (trail.json) and bolt (lightning.json) are no longer loaded (Vladimir:
  // "we use particle trail so this first one is redundant", "lightning we will
  // not use"). What still names them degrades quietly: emitTrail returns null
  // for an emitter the instance does not have, and every lightning call is
  // behind FXManager.isReady, which now stays false.
  return [
    // {
    //   name: "particle_trail_effect",
    //   bundle: `${BASE_PATH}/effects/trail/trail.json`,
    //   spritesheet: `${BASE_PATH}/effects/trail/trail_atlas.json`,
    // },
    // Zeus's `particle_trail` (`trail_coin`: soft_glow, additive) — the coin's
    // fire trail into the logo (Games/Shared/coinToLogoTrail). With trail.json
    // gone it is the FIRST trail bundle, so it creates the trail instance
    // itself (see the coin_trail_effect branch in assetsSlice).
    // {
    //   name: "coin_trail_effect",
    //   bundle: `${BASE_PATH}/effects/trail/particle_trail.json`,
    //   spritesheet: `${BASE_PATH}/effects/trail/particle_trail_atlas.json`,
    // },
    // ── The FLAME TRAIL (2026-10-06) ── one self-contained effect in
    // effects/flame_trail: flame_trail.json (the RevoltFX emitter
    // `flame_trail`: rotated fire frames, additive), flame_trail_atlas.json
    // (the fire particle + the 30 flame frames the tail drops) and
    // flame_trail.webp. Replaces Zeus's particle_trail and Mad Labs' trail.
    {
      name: "coin_trail_effect",
      bundle: `${BASE_PATH}/effects/flame_trail/flame_trail.json`,
      spritesheet: `${BASE_PATH}/effects/flame_trail/flame_trail_atlas.json`,
    },
    // Pot Collection's coin shower (`Falling_Coins`) — back for the Total Win
    // of free spins and Hold & Win (Vladimir, 2026-10-01). The only MAIN
    // bundle, so it is the one FXManager.init() loads: isReady turns true —
    // safe, every lightning call returns null without its emitter.
    {
      name: "coin_shower_effect",
      bundle: `${BASE_PATH}/effects/coin_shower/coin_shower.json`,
      spritesheet: `${BASE_PATH}/effects/coin_shower/coin_shower_atlas.json`,
    },
    // {
    //   name: "lightning_effect",
    //   bundle: `${BASE_PATH}/effects/lightning/lightning.json`,
    //   spritesheet: `${BASE_PATH}/effects/lightning/lightning_atlas.json`,
    // },
  ];
};

// ---------------------------------------------------------------------------
// 8. WEB FONTS — Custom font files for PIXI.Text (loaded via @font-face)
// ---------------------------------------------------------------------------
const buildWebFonts = () => {
  // The game's display font. One per game — Mad Labs uses Troika, Zeus used
  // GrekoNew. Not guarded by theme or by any feature flag: the game always
  // needs its own font.
  //
  // Registering here is what actually loads it. injectStyles builds the
  // @font-face from this list AND preloads every entry through the Font
  // Loading API, which matters because PIXI.Text rasterises to a canvas — a
  // font that has not finished loading bakes the fallback into the texture.
  return [
    // Troika — removed (Vladimir, 2026-09-30: "Troika can go"):
    // {
    //   name: "Troika",
    //   url: `${BASE_PATH}/fonts/troika/troika.otf`,
    //   format: "opentype",
    // },
    // Kanit Medium — the animator's coin / jackpot values style
    // ("fontFamily": "Kanit-Medium"). Google Fonts, OFL (fonts/kanit/OFL.txt).
    {
      name: "Kanit-Medium",
      url: `${BASE_PATH}/fonts/kanit/Kanit-Medium.ttf`,
      format: "truetype",
    },
    // Noto Sans Black under the NAME the animator's text styles use
    // ("fontFamily": "NotoSans-Black", 2026-09-30) — so their JSONs work as
    // delivered. The same file as "Noto Sans" 900 (from Mad Labs).
    {
      name: "NotoSans-Black",
      url: `${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-900.woff2`,
      format: "woff2",
    },
  ];
};



// ---------------------------------------------------------------------------
// 9. MASTER ASSETS OBJECT — consumed by assetsSlice.js
// ---------------------------------------------------------------------------
export const ASSETS = {
  bitmapFonts: buildBitmapFonts(),
  spineAnimations: buildSpineAnimations(),
  textures: [...UI_ASSETS, ...GAME_TEXTURES],
  effects: buildEffects(),
  webFonts: buildWebFonts(),
};

// ---------------------------------------------------------------------------
// 9. ASSET_NAME MAP — flat { name → url/json } for direct cache lookups
// ---------------------------------------------------------------------------
const generateAssetNames = (assets) => {
  const map = {};
  assets.textures.forEach((a) => {
    map[a.name] = a.url;
  });
  assets.spineAnimations.forEach((a) => {
    map[a.name] = a.json;
  });
  assets.bitmapFonts.forEach((a) => {
    map[a.name] = a.json;
  });
  assets.effects.forEach((a) => {
    map[a.name] = a.bundle;
  });
  return map;
};

export const ASSET_NAME = generateAssetNames(ASSETS);
